import { parseUrl } from 'utils/url';
import _ from '../utils/lodash';
import { generateCssSelector, generateGeneralCssSelector } from 'utils/css';
import { checkGeneralCondition } from 'utils/pathUtils';
import { getCSSSelectorRegEx } from 'utils/selectorRegex';
import validate, { ERROR } from 'utils/validate';

export const SELECTION_TYPE = {
  SINGLE: 'SINGLE',
  MANUAL: 'MANUAL',
  SIMILAR: 'SIMILAR'
};

export const CATEGORY = {
  URL: 'URL',
  DOM_ELEMENT: 'DOM_ELEMENT',
  API: 'API',
  COMBO: 'COMBO'
};

export const EVENT_TYPE = {
  PAGEVIEW: 'PAGEVIEW',
  CLICK: 'CLICK',
  CHANGE_VALUE_OR_PROPERTY: 'CHANGE_VALUE_OR_PROPERTY',
  SUBMIT_FORM: 'SUBMIT_FORM'
};

export const SCOPE = {
  GLOBAL: 'GLOBAL',
  LOCAL: 'LOCAL'
};

export default class Event {
  constructor (attrsOrDomElement, projectId, url, shadowRoot) {
    let currentPathCondition = window.location.href;
    if (attrsOrDomElement && attrsOrDomElement.currentContextPath &&
        attrsOrDomElement.currentContextPath.length > 0) {
      currentPathCondition = attrsOrDomElement.currentContextPath;
    }
    if (url) {
      currentPathCondition = url;
    }
    let defaultAttrs = {
      category: CATEGORY.DOM_ELEMENT,
      content: '',
      currentContextPath: currentPathCondition,
      definitionType: SELECTION_TYPE.SINGLE,
      eventType: EVENT_TYPE.CLICK,
      id: '',
      modifiedDt: '',
      name: '',
      projectId: '',
      serialized: '',
      scope: SCOPE.GLOBAL,
      snapshotProperties: [],
      startingPoint: ''
    };

    if (url) {
      defaultAttrs['pathCondition'] = url;
    }
    this.state = {
      error: ERROR.OK
    };

    let id = new Date().getTime();
    if (attrsOrDomElement instanceof HTMLElement || attrsOrDomElement instanceof SVGElement ||
        this.isHTMLElement(attrsOrDomElement)) {
      const domElement = attrsOrDomElement;
      const elText = this.getTextContent(domElement);
      this.attrs = {
        ...defaultAttrs,
        id,
        projectId,
        tempEventContent: elText
      };
      this.addElement(domElement, shadowRoot);
    } else {
      let {pathCondition, ...attrs} = {...defaultAttrs, ...attrsOrDomElement};
      if (pathCondition) {
        let didFail = false;
        try {
          attrs.pathCondition = JSON.parse(pathCondition);
        } catch (error) {
          didFail = true;
        }

        if (didFail) {
          try {
            attrs.pathCondition = pathCondition;
          } catch (error) {
          }
        }
      }
      this.attrs = attrs;
      if (projectId) {
        this.attrs.projectId = projectId;
      }
    }

    if (!this.serialized) {
      this.attrs.serialized = getCSSSelectorRegEx(this.cssSelector);
    }

    if (this.attrs.startingPoint && this.originalElement) {
      // keep the element's original position. This value will be used in case the element becomes hidden
      let rect = this.originalElement.getBoundingClientRect();
      this.originalRect = {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        scrollX: window.scrollX || window.pageXOffset,
        scrollY: window.scrollY || window.pageYOffset
      };
    }
  }

  // Be consistent with JSSDK getClickTargetText()
  getTextContent (domElement) {
    let elText = '';
    if (domElement.type === 'password') {
      elText = 'input[type=\"password\"]';
    } else if (domElement.nodeName.toLowerCase() === 'select') {
      elText = domElement.name;
    } else if (domElement.nodeName.toLowerCase() === 'img') {
      elText = domElement.alt;
    } else if (domElement.getAttribute && domElement.getAttribute('data-text')) {
      elText = domElement.getAttribute('data-text');
    }
    const value = (domElement.value || '') + '';
    elText = elText || value || domElement.textContent || domElement.innerText;
    // keep the first 60 chars
    let textLimit = 60;
    if (elText) {
      elText = elText.substring(0, textLimit);
    }
    return (elText||'').trim();
  }

  isHTMLElement (object) {
    let string = {}.toString.call(object);
    return string.startsWith('[object HTML') && string.endsWith('Element]');
  };

  querySelectorAllInShadowDOM = (nodeList, selectors) => {
    let results = [];
    _.each(nodeList, (currNode) => {
      if (currNode.shadowRoot) {
        const shadowResults = currNode.shadowRoot.querySelectorAll(selectors);
        // recursively check nested shadow
        const allNodes = currNode.shadowRoot.querySelectorAll('*');
        const descendentResults = this.querySelectorAllInShadowDOM(allNodes, selectors);
        if (shadowResults.length > 0 || descendentResults.length > 0) {
          results = [...results, ...shadowResults, ...descendentResults];
        }
      }
    });
    return results;
  }

  querySelectorAll = (node, selectors) => {
    try {
      if (!node || !selectors) return [];
      let results = node.querySelectorAll(selectors) || [];
      let allNodes = node.querySelectorAll('*');
      const shadowResults = this.querySelectorAllInShadowDOM(allNodes, selectors) || [];
      if (shadowResults.length > 0) {
        results = [...results, ...shadowResults];
      }
      return results;
    } catch (e) {
      console.log('Exception in querySelectorAll', e);
    }
  }

  get currentContextPath () {
    return this.attrs.currentContextPath;
  }

  get customized () {
    return this.state.origCssSelector && this.state.origCssSelector !== this.cssSelector;
  }

  get cssSelector () {
    return this.attrs.content;
  }

  set cssSelector (selector) {
    this.attrs.content = selector;
    this.state.error = validate(selector);
    if (this.state.error === ERROR.OK) {
      this.attrs.serialized = getCSSSelectorRegEx(selector);
    }
  }

  get elements () {
    if (this.state.error !== ERROR.OK) {
      return [];
    }

    if (this.attrs.scope === SCOPE.local) {
      const parsedUrl = parseUrl(this.attrs.currentContextPath);
      if (parsedUrl.host !== window.location.host || parsedUrl.pathname !== window.location.pathname) {
        return [];
      }
    }
    return Array.from(this.querySelectorAll(document, this.cssSelector)).filter(el => {
      for (let curEl = el; curEl !== document.body && curEl.parentNode; curEl = curEl.parentNode) {
        if (curEl.className === 'aurycEventEditor') {
          return false;
        }
      }

      const elText = this.getTextContent(el);
      if (!_.isEmpty(this.attrs.pathCondition)) {
        for (let i = 0; i < this.attrs.pathCondition.length; i++) {
          if (!checkGeneralCondition(this.attrs.pathCondition[i])) return false;
        }
        if (this.attrs.eventContent && this.attrs.eventContent.length && this.attrs.eventContent !== elText) {
          return false;
        }
        return true;
      } else if (this.attrs.eventContent && this.attrs.eventContent !== elText) {
        return false;
      }
      return true;
    });
  }

  get id () {
    return this.attrs.id;
  }

  get name () {
    return this.attrs.name;
  }

  set name (name) {
    this.attrs.name = name;
  }

  get originalElement () {
    try {
      return document.querySelector(this.attrs.startingPoint);
    } catch (e) {
      return document.querySelector('body');
    }
  }

  get restrictToUrl () {
    return this.attrs.scope === SCOPE.LOCAL;
  }

  set restrictToUrl (singlePage) {
    this.attrs.scope = singlePage ? SCOPE.LOCAL : SCOPE.GLOBAL;
  }

  get scope () {
    return this.attrs.scope;
  }

  set scope (scope) {
    this.attrs.scope = scope;
  }

  get selectionType () {
    return this.attrs.definitionType;
  }

  set selectionType (type) {
    // if (this.state.origCssSelector) {
    //   // cannot change selection type when the css selector is customized.
    //   return;
    // }
    switch (type) {
      case SELECTION_TYPE.SINGLE:
      case SELECTION_TYPE.MANUAL:
        if (this.attrs.definitionType !== type) {
          this.cssSelector = this.attrs.startingPoint;
        }
        break;
      case SELECTION_TYPE.SIMILAR:
        if (this.attrs.definitionType !== type) {
          let srcElem = document.querySelector(this.attrs.startingPoint) || this.attrs.startingEl;
          this.cssSelector = srcElem ? generateGeneralCssSelector(srcElem) : this.attrs.startingPoint;
        }
        break;
      default:
        throw new TypeError(`Invalid selection type '${type}'`);
    }
    this.attrs.definitionType = type;
  }

  get serialized () {
    return this.attrs.serialized;
  }

  get valid () {
    return this.state.error === ERROR.OK;
  }

  addElement (el, shadowRoot) {
    const cssSelector = generateCssSelector(el, undefined, shadowRoot);
    this.addToContent(cssSelector, el);
  }

  addToContent (cssSelector, el) {
    cssSelector = cssSelector.trim();
    if (cssSelector === '') {
      return;
    }

    if (this.cssSelector.split(',').indexOf(cssSelector) >= 0) {
      return;
    }

    let newSelector = this.cssSelector;
    if (newSelector !== '') {
      newSelector += ',';
    } else {
      this.attrs.startingPoint = cssSelector;
      if (el) this.attrs.startingEl = el;
    }
    newSelector += cssSelector;

    this.cssSelector = cssSelector;
  }

  setCustomCssSelector (selector) {
    if (selector && selector === this.state.origCssSelector) {
      this.removeCustomCssSelector();
      return;
    }
    if (!this.state.origCssSelector) {
      this.state.origCssSelector = this.cssSelector;
    }
    this.state.error = validate(selector);
    this.cssSelector = selector;
  }

  removeCustomCssSelector () {
    if (this.state.origCssSelector) {
      this.cssSelector = this.state.origCssSelector;
    }
    this.state.error = ERROR.OK;
    delete this.state.origCssSelector;
  }

  get origCssSelector () {
    return this.state.origCssSelector;
  }

  setState (name, value) {
    this.state[name] = value;
  }

  getState (name) {
    return this.state[name];
  }

  toggleElement (el) {
    const cssSelector = generateCssSelector(el);
    let selectors = this.cssSelector.split(',');

    let index = selectors.indexOf(cssSelector);
    // it is not allowed to delete the first element (index === 0)
    if (index > 0) {
      selectors.splice(index, 1);
    } else if (index < 0) {
      selectors.push(cssSelector);
    }
    this.cssSelector = selectors.join(',');
  }

  get deepCopy () {
    const copy = new Event();
    copy.attrs = Object.assign({}, this.attrs);
    copy.attrs.snapshotProperties = this.attrs.snapshotProperties.map(prop => prop.deepCopy);
    copy.originalRect = Object.assign({}, this.originalRect);
    copy.state = Object.assign({}, this.state);
    return copy;
  }
}



/** WEBPACK FOOTER **
 ** ./src/model/Event.js
 **/